import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  BellRing, 
  X, 
  Check, 
  CheckCheck, 
  Trash2,
  Settings,
  Clock,
  AlertCircle,
  Info,
  CheckCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { api } from '../../services/api';
import { useApiCall } from '../../hooks/useApiCall';
import { showSuccess } from '../../utils/toast';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  data?: Record<string, any>;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  expires_at?: string;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // API calls
  const fetchNotifications = useApiCall(async (pageNum: number, unreadOnly: boolean) => {
    const response = await api.get('/notifications', {
      params: {
        page: pageNum,
        page_size: 20,
        unread_only: unreadOnly
      }
    });
    return response.data;
  });

  const fetchUnreadCount = useApiCall(async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  });

  const markAsRead = useApiCall(async (notificationId: number) => {
    await api.put(`/notifications/${notificationId}/read`, {});
  });

  const markAllAsRead = useApiCall(async () => {
    await api.put('/notifications/read-all', {});
  });

  const deleteNotification = useApiCall(async (notificationId: number) => {
    await api.delete(`/notifications/${notificationId}`);
  });

  // Load notifications on mount and tab change
  useEffect(() => {
    if (isOpen) {
      loadNotifications(true);
      loadUnreadCount();
    }
  }, [isOpen, activeTab]);

  const loadNotifications = async (reset = false) => {
    const pageToLoad = reset ? 1 : page;
    const unreadOnly = activeTab === 'unread';

    const result = await fetchNotifications.execute(pageToLoad, unreadOnly);
    if (result) {
      if (reset) {
        setNotifications(result.notifications);
        setPage(2);
      } else {
        setNotifications(prev => [...prev, ...result.notifications]);
        setPage(prev => prev + 1);
      }
      
      const totalPages = result.pagination.total_pages;
      setHasMore(pageToLoad < totalPages);
    }
  };

  const loadUnreadCount = async () => {
    const result = await fetchUnreadCount.execute();
    if (result) {
      setUnreadCount(result.unread_count);
    }
  };

  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.is_read) return;

    await markAsRead.execute(notification.id);
    
    setNotifications(prev => 
      prev.map(n => 
        n.id === notification.id 
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n
      )
    );
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead.execute();
    
    setNotifications(prev => 
      prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
    );
    
    setUnreadCount(0);
    showSuccess('تم تحديد جميع الإشعارات كمقروءة');
  };

  const handleDelete = async (notificationId: number) => {
    await deleteNotification.execute(notificationId);
    
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    showSuccess('تم حذف الإشعار');
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'job_card_completed':
      case 'payment_received':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'invoice_overdue':
      case 'subscription_expiring':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'job_card_status_update':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'service_reminder':
        return <Clock className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'job_card_completed':
      case 'payment_received':
        return 'border-r-green-500';
      case 'invoice_overdue':
      case 'subscription_expiring':
        return 'border-r-yellow-500';
      case 'job_card_status_update':
        return 'border-r-blue-500';
      case 'service_reminder':
        return 'border-r-purple-500';
      default:
        return 'border-r-gray-300';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Notification Panel */}
      <div className="fixed left-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <BellRing className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">الإشعارات</h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-1 hover:bg-gray-200 rounded-full"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium border-b-2 ${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            الكل
          </button>
          <button
            onClick={() => setActiveTab('unread')}
            className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium border-b-2 ${
              activeTab === 'unread'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            غير مقروءة ({unreadCount})
          </button>
        </div>

        {/* Actions */}
        {unreadCount > 0 && (
          <div className="p-2.5 sm:p-3 border-b bg-gray-50">
            <button
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.loading}
              className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
            >
              <CheckCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>تحديد الكل كمقروء</span>
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {fetchNotifications.loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Bell className="w-8 h-8 mb-2" />
              <p className="text-sm">لا توجد إشعارات</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 sm:p-4 hover:bg-gray-50 cursor-pointer border-r-4 ${
                    getNotificationColor(notification.type)
                  } ${!notification.is_read ? 'bg-blue-50' : ''}`}
                  onClick={() => handleMarkAsRead(notification)}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h4 className={`text-xs sm:text-sm font-medium text-gray-900 ${
                          !notification.is_read ? 'font-semibold' : ''
                        }`}>
                          {notification.title}
                        </h4>
                        
                        <div className="flex items-center space-x-1 sm:space-x-1 ml-1 sm:ml-2">
                          {!notification.is_read && (
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full"></div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notification.id);
                            }}
                            className="p-0.5 sm:p-1 text-gray-400 hover:text-red-500 rounded"
                          >
                            <Trash2 className="w-3 h-3 sm:w-3 sm:h-3" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 leading-relaxed">
                        {notification.message}
                      </p>
                      
                      <p className="text-[10px] sm:text-xs text-gray-400 mt-1.5 sm:mt-2">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: ar
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More */}
              {hasMore && (
                <div className="p-4 text-center">
                  <button
                    onClick={() => loadNotifications(false)}
                    disabled={fetchNotifications.loading}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    {fetchNotifications.loading ? 'جاري التحميل...' : 'تحميل المزيد'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-2.5 sm:p-3 border-t bg-gray-50">
          <button className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600 hover:text-gray-700">
            <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>إعدادات الإشعارات</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;